import dayjs from "utils/dayjs";

export type ReceiptAsset = {
  __typename: string;
  assetUrl: string;
  id: string;
  extension: string;
  filename?: string;
};

export type Receipt = {
  __typename: string;
  id: string;
  companyName: string;
  amount: number;
  transactionDate: number;
  isMatchTransaction: boolean;
  processingStatus?: "QUEUED" | "PROCESSING" | "COMPLETE" | "FAILED" | null;
  currency: string;
  assets: ReceiptAsset[];
  user: {
    firstName: string;
    lastName?: string | null;
    profilePicUrl?: string | null;
  };
  createdAt?: number;
};

export function isReceiptProcessing(receipt: {
  processingStatus?: string | null;
}): boolean {
  return (
    receipt.processingStatus === "QUEUED" ||
    receipt.processingStatus === "PROCESSING"
  );
}

/**
 * Coerce amount for scan completeness (matches server `isReceiptScanFailed` on numeric rows;
 * tolerates string amounts from JSON / display formatting so "0", "0.00", "0,00" all read as zero).
 */
function amountIsMissingOrZero(
  amount: number | string | null | undefined,
): boolean {
  if (amount == null) return true;
  if (typeof amount === "number")
    return !Number.isFinite(amount) || amount === 0;
  const s = String(amount).trim();
  if (s === "") return true;
  const n = parseFloat(s.replace(/,/g, ""));
  return !Number.isFinite(n) || n === 0;
}

/** True when the receipt has no usable transaction date (aligned with scan-failed date checks). */
export function isReceiptTransactionDateMissing(
  transactionDate: number | null | undefined,
): boolean {
  if (transactionDate == null) return true;
  if (transactionDate === 0) return true;
  if (typeof transactionDate === "number" && Number.isNaN(transactionDate))
    return true;
  return false;
}

/** Matches server `isReceiptScanFailed`: merchant OR date OR amount missing/zero. */
export function isReceiptScanFailed(receipt: {
  processingStatus?: string | null;
  companyName?: string | null;
  amount?: number | string | null;
  transactionDate?: number | null;
}): boolean {
  if (isReceiptProcessing(receipt)) return false;
  if (receipt.processingStatus === "FAILED") return true;
  const noMerchant = !receipt.companyName?.trim();
  const noDate = isReceiptTransactionDateMissing(receipt.transactionDate);
  const noAmount = amountIsMissingOrZero(receipt.amount);
  return noMerchant || noDate || noAmount;
}

/** The library's tabs in order, as the GraphQL `ReceiptLibraryFilter` values. */
export const RECEIPT_LIBRARY_FILTERS = [
  "ALL",
  "UNMATCHED",
  "SCAN_FAILED",
] as const;

export type ReceiptLibraryFilter = (typeof RECEIPT_LIBRARY_FILTERS)[number];

export type ReceiptDayGroup = {
  /** Local calendar day `YYYY-MM-DD`, or `__none__` when `createdAt` is missing */
  dayKey: string;
  label: string;
  receipts: Receipt[];
};

function sortReceiptsWithinDay(a: Receipt, b: Receipt): number {
  const ca = a.createdAt ?? 0;
  const cb = b.createdAt ?? 0;
  if (cb !== ca) return cb - ca;
  return b.id.localeCompare(a.id);
}

/**
 * Group library receipts by creation calendar day (local TZ). Days are newest-first;
 * receipts without `createdAt` are placed last in an "unknown" bucket.
 */
export function groupReceiptsByCreationDay(
  receipts: Receipt[],
  unknownCreationDateLabel: string,
  /** Locale for section titles, e.g. `en` or `es` */
  locale: string = "en",
): ReceiptDayGroup[] {
  const unknown: Receipt[] = [];
  const byDay = new Map<string, Receipt[]>();

  for (const r of receipts) {
    if (r.createdAt == null || r.createdAt === 0) {
      unknown.push(r);
    } else {
      const dayKey = dayjs(r.createdAt).format("YYYY-MM-DD");
      const list = byDay.get(dayKey);
      if (list) list.push(r);
      else byDay.set(dayKey, [r]);
    }
  }

  const sortedDayKeys = Array.from(byDay.keys()).sort((a, b) =>
    b.localeCompare(a),
  );

  const groups: ReceiptDayGroup[] = sortedDayKeys.map((dayKey) => ({
    dayKey,
    label: dayjs(dayKey, "YYYY-MM-DD").locale(locale).format("MMMM D, YYYY"),
    receipts: [...(byDay.get(dayKey) ?? [])].sort(sortReceiptsWithinDay),
  }));

  if (unknown.length > 0) {
    groups.push({
      dayKey: "__none__",
      label: unknownCreationDateLabel,
      receipts: [...unknown].sort(sortReceiptsWithinDay),
    });
  }

  return groups;
}

/** Valid index into `assets` for viewing; safe when length is 0. */
export function clampReceiptAssetIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(0, index), length - 1);
}

/**
 * After removing the asset at `deletedIndex`, choose a stable viewer index
 * for the list length *before* the delete.
 */
export function adjustReceiptAssetIndexAfterDelete(
  prevIndex: number,
  deletedIndex: number,
  lengthBeforeDelete: number,
): number {
  const newCount = lengthBeforeDelete - 1;
  if (deletedIndex < prevIndex) return prevIndex - 1;
  if (deletedIndex === prevIndex) {
    return Math.min(deletedIndex, Math.max(0, newCount - 1));
  }
  return prevIndex;
}
